import * as THREE from 'three';

/**
 * Google Maps-style navigation controls
 * - Drag to rotate (mouse + touch)
 * - Scroll/pinch to zoom
 * - WASD + Q/E for keyboard movement
 */
export class MapNavigationControls {
  constructor(camera, domElement) {
    this.camera = camera;
    this.domElement = domElement;
    
    // Rotation state
    this.spherical = new THREE.Spherical();
    this.target = new THREE.Vector3(0, 0, 0);
    this.rotateSpeed = 0.005;
    this.zoomSpeed = 0.1;
    this.minDistance = 1;
    this.maxDistance = 100;
    this.minPolarAngle = 0.1;
    this.maxPolarAngle = Math.PI * 0.85;
    
    // Interaction state
    this.isDragging = false;
    this.isPinching = false;
    this.wasActualDrag = false; // True if mouse actually moved during drag
    this.dragStart = new THREE.Vector2();
    this.dragDelta = new THREE.Vector2();
    this.totalDragDistance = 0;
    this.dragThreshold = 5; // Pixels moved before considered a drag
    this.lastTouchDistance = 0;
    this.touchStartTime = 0;
    this.touchMoved = false;
    
    // Keyboard state
    this.keys = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      up: false,
      down: false
    };
    this.moveSpeed = 5;
    
    // Initialize spherical coordinates from camera position
    this.updateSphericalFromCamera();
    
    // Bind event handlers
    this.setupEventListeners();
  }

  updateSphericalFromCamera() {
    const offset = new THREE.Vector3();
    offset.copy(this.camera.position).sub(this.target);
    this.spherical.setFromVector3(offset);
  }

  setupEventListeners() {
    // Mouse events
    this.domElement.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.domElement.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.domElement.addEventListener('mouseup', (e) => this.onMouseUp(e));
    this.domElement.addEventListener('mouseleave', (e) => this.onMouseUp(e));
    this.domElement.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    
    // Touch events
    this.domElement.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
    this.domElement.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
    this.domElement.addEventListener('touchend', (e) => this.onTouchEnd(e));
    
    // Prevent context menu on right-click
    this.domElement.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // Mouse handlers
  onMouseDown(event) {
    if (event.button === 0 || event.button === 2) {
      this.isDragging = true;
      this.wasActualDrag = false;
      this.totalDragDistance = 0;
      this.dragStart.set(event.clientX, event.clientY);
      this.domElement.style.cursor = 'grabbing';
    }
  }

  onMouseMove(event) {
    if (!this.isDragging) return;
    
    this.dragDelta.set(
      event.clientX - this.dragStart.x,
      event.clientY - this.dragStart.y
    );
    
    // Track total distance moved
    this.totalDragDistance += Math.abs(this.dragDelta.x) + Math.abs(this.dragDelta.y);
    
    // Only rotate if we've moved past threshold
    if (this.totalDragDistance > this.dragThreshold) {
      this.wasActualDrag = true;
      this.rotateCamera(this.dragDelta.x, this.dragDelta.y);
    }
    
    this.dragStart.set(event.clientX, event.clientY);
  }

  onMouseUp(event) {
    // Keep wasActualDrag true briefly so click handler can check it
    const wasDrag = this.wasActualDrag;
    this.isDragging = false;
    this.domElement.style.cursor = 'grab';
    
    // Reset wasActualDrag after a short delay so click event can read it
    setTimeout(() => {
      this.wasActualDrag = false;
    }, 50);
  }

  onWheel(event) {
    event.preventDefault();
    
    const delta = event.deltaY > 0 ? 1 : -1;
    this.zoom(delta);
  }

  // Touch handlers
  onTouchStart(event) {
    event.preventDefault();
    
    this.touchStartTime = Date.now();
    this.touchMoved = false;
    
    if (event.touches.length === 1) {
      // Single touch - prepare for drag
      this.isDragging = true;
      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    } else if (event.touches.length === 2) {
      // Two fingers - pinch zoom
      this.isPinching = true;
      this.isDragging = false;
      this.lastTouchDistance = this.getTouchDistance(event.touches);
    }
  }

  onTouchMove(event) {
    event.preventDefault();
    this.touchMoved = true;
    
    if (event.touches.length === 1 && this.isDragging) {
      // Single touch drag - rotate
      this.dragDelta.set(
        event.touches[0].clientX - this.dragStart.x,
        event.touches[0].clientY - this.dragStart.y
      );
      
      this.rotateCamera(this.dragDelta.x, this.dragDelta.y);
      
      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    } else if (event.touches.length === 2 && this.isPinching) {
      // Pinch zoom
      const distance = this.getTouchDistance(event.touches);
      const delta = this.lastTouchDistance - distance;
      this.zoom(delta * 0.01);
      this.lastTouchDistance = distance;
    }
  }

  onTouchEnd(event) {
    // Check if it was a tap (short touch without movement)
    const touchDuration = Date.now() - this.touchStartTime;
    const wasTap = touchDuration < 300 && !this.touchMoved;
    
    if (event.touches.length === 0) {
      this.isDragging = false;
      this.isPinching = false;
    } else if (event.touches.length === 1) {
      this.isPinching = false;
      this.isDragging = true;
      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    }
  }

  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Camera manipulation
  rotateCamera(deltaX, deltaY) {
    // Horizontal rotation (theta)
    this.spherical.theta -= deltaX * this.rotateSpeed;
    
    // Vertical rotation (phi) with limits
    this.spherical.phi += deltaY * this.rotateSpeed;
    this.spherical.phi = Math.max(
      this.minPolarAngle,
      Math.min(this.maxPolarAngle, this.spherical.phi)
    );
    
    this.updateCameraPosition();
  }

  zoom(delta) {
    this.spherical.radius += delta * this.zoomSpeed * this.spherical.radius;
    this.spherical.radius = Math.max(
      this.minDistance,
      Math.min(this.maxDistance, this.spherical.radius)
    );
    
    this.updateCameraPosition();
  }

  updateCameraPosition() {
    const offset = new THREE.Vector3();
    offset.setFromSpherical(this.spherical);
    
    this.camera.position.copy(this.target).add(offset);
    this.camera.lookAt(this.target);
  }

  // Keyboard handlers
  onKeyDown(event) {
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.keys.forward = true;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.keys.backward = true;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.keys.left = true;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.keys.right = true;
        break;
      case 'KeyQ':
        this.keys.down = true;
        break;
      case 'KeyE':
        this.keys.up = true;
        break;
    }
  }

  onKeyUp(event) {
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.keys.forward = false;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.keys.backward = false;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.keys.left = false;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.keys.right = false;
        break;
      case 'KeyQ':
        this.keys.down = false;
        break;
      case 'KeyE':
        this.keys.up = false;
        break;
    }
  }

  update(delta) {
    // Handle keyboard movement
    const moveVector = new THREE.Vector3();
    
    // Get camera direction (horizontal only)
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();
    
    const right = new THREE.Vector3();
    right.crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
    
    if (this.keys.forward) moveVector.add(forward);
    if (this.keys.backward) moveVector.sub(forward);
    if (this.keys.right) moveVector.add(right);
    if (this.keys.left) moveVector.sub(right);
    if (this.keys.up) moveVector.y += 1;
    if (this.keys.down) moveVector.y -= 1;
    
    if (moveVector.length() > 0) {
      moveVector.normalize().multiplyScalar(this.moveSpeed * delta);
      
      // Move both camera and target
      this.camera.position.add(moveVector);
      this.target.add(moveVector);
    }
  }

  // Set target position (for click-to-move)
  setTarget(position) {
    const offset = new THREE.Vector3();
    offset.copy(this.camera.position).sub(this.target);
    
    this.target.copy(position);
    this.camera.position.copy(this.target).add(offset);
  }

  dispose() {
    // Remove event listeners
    this.domElement.removeEventListener('mousedown', this.onMouseDown);
    this.domElement.removeEventListener('mousemove', this.onMouseMove);
    this.domElement.removeEventListener('mouseup', this.onMouseUp);
    this.domElement.removeEventListener('wheel', this.onWheel);
    this.domElement.removeEventListener('touchstart', this.onTouchStart);
    this.domElement.removeEventListener('touchmove', this.onTouchMove);
    this.domElement.removeEventListener('touchend', this.onTouchEnd);
  }
}
